/**
 * usePlanTimeline — resolves the plan-timeline for the active planner level by
 * dispatching to the matching Oxbo-P endpoint (§10.9): fleet → /fleet, pad →
 * /pads/{site}, well → /wells/{uwi}, section → /events/{event}. Scope comes from
 * the current well context; only the query for the active level actually fires
 * (the others are `skip`ped) so we never over-fetch.
 *
 * Every span/KPI in the response is server-computed (§ P owns the numbers); this
 * hook only picks the right endpoint and surfaces load/error/empty state.
 */
import { useCurrentWell } from "@hooks/useCurrentWell";
import {
  useGetFleetPlanTimelineQuery,
  useGetPadPlanTimelineQuery,
  useGetWellPlanTimelineQuery,
  useGetEventPlanTimelineQuery,
} from "@api/pApi";
import type { PlanTimelineResponse } from "@api/types";
import type { PlannerLevel } from "./plannerModel";

/**
 * The pad/site a well belongs to. WellView doesn't yet carry its site linkage in
 * the wire contract, so we fall back to the seed pad until Oxbo-M exposes it;
 * this is the single place that resolves it, ready to swap for `well.site`.
 */
const DEFAULT_SITE_ID = "site-nesson";

export interface PlanTimelineState {
  data: PlanTimelineResponse | undefined;
  /** The scope id the active level resolved to (for the breadcrumb). */
  scope: string | undefined;
  wellName: string | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function usePlanTimeline(level: PlannerLevel): PlanTimelineState {
  const { well, activeEvent } = useCurrentWell();

  const siteId = DEFAULT_SITE_ID;
  const uwi = well?.uwi ?? "";
  const eventId = activeEvent?.id ?? "";

  // Only the active level's query runs; the rest are skipped.
  const fleet = useGetFleetPlanTimelineQuery(undefined, { skip: level !== "fleet" });
  const pad = useGetPadPlanTimelineQuery(siteId, { skip: level !== "pad" });
  const wellQ = useGetWellPlanTimelineQuery(uwi, { skip: level !== "well" || !uwi });
  const section = useGetEventPlanTimelineQuery(eventId, {
    skip: level !== "section" || !eventId,
  });

  const active =
    level === "fleet"
      ? { q: fleet, scope: "fleet" }
      : level === "pad"
        ? { q: pad, scope: siteId }
        : level === "well"
          ? { q: wellQ, scope: uwi }
          : { q: section, scope: eventId };

  return {
    data: active.q.data,
    scope: active.scope || undefined,
    wellName: well?.commonName,
    isLoading: active.q.isLoading,
    isError: active.q.isError,
    refetch: active.q.refetch,
  };
}
